import { defineComponent as _defineComponent } from 'vue'
import { normalizeStyle as _normalizeStyle, createElementVNode as _createElementVNode, withModifiers as _withModifiers, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, toDisplayString as _toDisplayString, vModelSelect as _vModelSelect, withDirectives as _withDirectives, vModelCheckbox as _vModelCheckbox, unref as _unref, createCommentVNode as _createCommentVNode } from "vue"

const _hoisted_1 = { class: "sidebar-price-range px-2 py-4" }
const _hoisted_2 = { class: "flex justify-between gap-4" }
const _hoisted_3 = { class: "flex-1" }
const _hoisted_4 = ["value"]
const _hoisted_5 = { class: "flex-1 text-right" }
const _hoisted_6 = ["value"]
const _hoisted_7 = {
  key: 0,
  class: "pt-2"
}
const _hoisted_8 = { class: "flex items-start gap-3 cursor-pointer group" }
const _hoisted_9 = { class: "relative flex items-center justify-center" }
const _hoisted_10 = { class: "flex flex-col" }
const _hoisted_11 = { class: "text-sm font-semibold text-gray-900 group-hover:text-brand-main transition-colors" }
const _hoisted_12 = { class: "text-[11px] text-gray-500 leading-relaxed mt-0.5" }
const _hoisted_13 = {
  key: 1,
  class: "pt-2"
}
const _hoisted_14 = { class: "flex items-start gap-3 cursor-pointer group" }
const _hoisted_15 = { class: "relative flex items-center justify-center" }
const _hoisted_16 = { class: "flex flex-col" }
const _hoisted_17 = { class: "text-sm font-semibold text-gray-900 group-hover:text-brand-main transition-colors" }
const _hoisted_18 = { class: "text-[11px] text-gray-500 leading-relaxed mt-0.5" }

import {computed, onMounted, onUnmounted, ref, watch} from "vue";
import {useI18n} from 'vue-i18n';


export default /*@__PURE__*/_defineComponent({
  __name: 'sidebarPriceRange',
  props: {
    filter: { default: () => ({}) },
    activeValue: { default: "" },
    activePriceSort: { default: "" },
    catalogItemType: { default: '' }
  },
  emits: ["update", "update:priceSort"],
  setup(__props: any, { emit: __emit }) {

const props = __props;

const emit = __emit;

const {t} = useI18n();

const isMaxStoneSize = computed({
  get: () => props.activePriceSort === 'max-stone-size',
  set: (val: boolean) => {
    emit('update:priceSort', val ? 'max-stone-size' : '');
  }
});

const isMinPrice = computed({
  get: () => props.activePriceSort === 'min-price',
  set: (val: boolean) => {
    emit('update:priceSort', val ? 'min-price' : '');
  }
});

const min = computed(() => props.filter.min ?? 0);
const max = computed(() => props.filter.max ?? 10000);


// Parse activeValue "min-max"
const parsedActiveValue = computed(() => {
  if (!props.activeValue) return {min: min.value, max: max.value};
  const parts = props.activeValue.split('-');
  if (parts.length !== 2) return {min: min.value, max: max.value};
  return {
    min: parseFloat(parts[0]),
    max: parseFloat(parts[1])
  };
});

const sliderRef = ref<HTMLElement | null>(null);
const isDragging = ref<'min' | 'max' | null>(null);

const localMin = ref(parsedActiveValue.value.min);
const localMax = ref(Math.max(parsedActiveValue.value.max, 300));

watch(() => props.activeValue, () => {
  if (!isDragging.value) {
    localMin.value = parsedActiveValue.value.min;
    localMax.value = Math.max(parsedActiveValue.value.max, 300);
  }
});

const currentMin = computed({
  get: () => localMin.value,
  set: (val) => {
    localMin.value = Math.min(val, localMax.value);
    emitUpdate();
  }
});

const currentMax = computed({
  get: () => localMax.value,
  set: (val) => {
    localMax.value = Math.max(val, localMin.value, 300);
    emitUpdate();
  }
});

const emitUpdate = () => {
  if (localMin.value === min.value && localMax.value === max.value) {
    emit("update", "");
  } else {
    emit("update", `${localMin.value}-${localMax.value}`);
  }
}

const getPercentage = (value: number) => {
  return ((value - min.value) / (max.value - min.value)) * 100;
};

const minPercent = computed(() => getPercentage(currentMin.value));
const maxPercent = computed(() => getPercentage(currentMax.value));

const handleMouseDown = (type: 'min' | 'max') => {
  isDragging.value = type;
};

const handleMouseMove = (e: MouseEvent | TouchEvent) => {
  if (!isDragging.value || !sliderRef.value) return;

  const rect = sliderRef.value.getBoundingClientRect();
  const clientX = 'touches' in e ? e.touches[0].clientX : (e as MouseEvent).clientX;
  const offsetX = clientX - rect.left;
  const percentage = Math.max(0, Math.min(100, (offsetX / rect.width) * 100));
  let value = (percentage / 100) * (max.value - min.value) + min.value;

  // Snap to step or options
  value = selectOptions.value.reduce((prev, curr) => {
    return (Math.abs(curr - value) < Math.abs(prev - value) ? curr : prev);
  });

  value = Number(value.toFixed(2));

  if (isDragging.value === 'min') {
    localMin.value = Math.min(value, localMax.value);
  } else {
    localMax.value = Math.max(value, localMin.value, 300);
  }
};

const handleMouseUp = () => {
  if (isDragging.value) {
    emitUpdate();
  }
  isDragging.value = null;
};

onMounted(() => {
  window.addEventListener('mousemove', handleMouseMove);
  window.addEventListener('mouseup', handleMouseUp);
  window.addEventListener('touchmove', handleMouseMove, {passive: false});
  window.addEventListener('touchend', handleMouseUp);
});

onUnmounted(() => {
  window.removeEventListener('mousemove', handleMouseMove);
  window.removeEventListener('mouseup', handleMouseUp);
  window.removeEventListener('touchmove', handleMouseMove);
  window.removeEventListener('touchend', handleMouseUp);
});

const selectOptions = computed(() => {
  const options = [];
  let cur = min.value;
  while (cur <= max.value) {
    options.push(Number(cur.toFixed(2)));
    if (cur < 2000) cur += 100;
    else if (cur < 10000) cur += 500;
    else if (cur < 100000) cur += 5000;
    else cur += 25000;
  }

  if (options[options.length - 1] !== max.value) {
    options.push(max.value);
  }
  return options;
});

const isMaxItemButtonAllowed = computed(() => {
  return [
    "engagement_ring",
    "engagement_ring_rings_of_the_stars",
  ].includes(props.catalogItemType)
})

const isMinPriceButtonAllowed = computed(() => {
  return props.catalogItemType.includes('wedding_ring')
})


return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("div", {
      class: "relative w-full h-1 bg-gray-200 rounded-full mb-8 mt-2",
      ref_key: "sliderRef",
      ref: sliderRef
    }, [
      _createElementVNode("div", {
        class: "absolute h-full bg-brand-main rounded-full",
        style: _normalizeStyle({ left: minPercent.value + '%', right: (100 - maxPercent.value) + '%' })
      }, null, 4),
      _createElementVNode("div", {
        class: "absolute top-1/2 -translate-y-1/2 -translate-x-1/2 w-4 h-4 bg-brand-main border-2 border-brand-main rounded-full cursor-pointer z-10 touch-none shadow-sm hover:scale-110 transition-transform",
        style: _normalizeStyle({ left: minPercent.value + '%' }),
        onMousedown: _cache[0] || (_cache[0] = ($event: any) => (handleMouseDown('min'))),
        onTouchstart: _cache[1] || (_cache[1] = _withModifiers(($event: any) => (handleMouseDown('min')), ["prevent"]))
      }, null, 36),
      _createElementVNode("div", {
        class: "absolute top-1/2 -translate-y-1/2 -translate-x-1/2 w-4 h-4 bg-brand-main border-2 border-brand-main rounded-full cursor-pointer z-10 touch-none shadow-sm hover:scale-110 transition-transform",
        style: _normalizeStyle({ left: maxPercent.value + '%' }),
        onMousedown: _cache[2] || (_cache[2] = ($event: any) => (handleMouseDown('max'))),
        onTouchstart: _cache[3] || (_cache[3] = _withModifiers(($event: any) => (handleMouseDown('max')), ["prevent"]))
      }, null, 36)
    ], 512),
    _createElementVNode("div", _hoisted_2, [
      _createElementVNode("div", _hoisted_3, [
        _cache[8] || (_cache[8] = _createElementVNode("label", { class: "block text-[10px] text-gray-400 uppercase tracking-wider mb-1" }, "Von", -1)),
        _withDirectives(_createElementVNode("select", {
          "onUpdate:modelValue": _cache[4] || (_cache[4] = ($event: any) => ((currentMin).value = $event)),
          class: "w-full border-b border-gray-200 py-1 text-sm outline-none focus:border-brand-main transition-colors appearance-none bg-transparent"
        }, [
          (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(selectOptions.value.filter(o => o <= currentMax.value), (opt) => {
            return (_openBlock(), _createElementBlock("option", {
              key: 'min-'+opt,
              value: opt
            }, _toDisplayString(opt === max.value ? '∞' : opt.toLocaleString('de-DE')) + " € ", 9, _hoisted_4))
          }), 128))
        ], 512), [
          [
            _vModelSelect,
            currentMin.value,
            void 0,
            { number: true }
          ]
        ])
      ]),
      _createElementVNode("div", _hoisted_5, [
        _cache[9] || (_cache[9] = _createElementVNode("label", { class: "block text-[10px] text-gray-400 uppercase tracking-wider mb-1" }, "Bis", -1)),
        _withDirectives(_createElementVNode("select", {
          "onUpdate:modelValue": _cache[5] || (_cache[5] = ($event: any) => ((currentMax).value = $event)),
          class: "w-full border-b border-gray-200 py-1 text-sm outline-none focus:border-brand-main transition-colors appearance-none bg-transparent text-right"
        }, [
          (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(selectOptions.value.filter(o => o >= currentMin.value && o >= 300), (opt) => {
            return (_openBlock(), _createElementBlock("option", {
              key: 'max-'+opt,
              value: opt
            }, _toDisplayString(opt === max.value ? '∞' : opt.toLocaleString('de-DE')) + " € ", 9, _hoisted_6))
          }), 128))
        ], 512), [
          [
            _vModelSelect,
            currentMax.value,
            void 0,
            { number: true }
          ]
        ])
      ])
    ]),
    (isMaxItemButtonAllowed.value)
      ? (_openBlock(), _createElementBlock("div", _hoisted_7, [
          _createElementVNode("label", _hoisted_8, [
            _createElementVNode("div", _hoisted_9, [
              _withDirectives(_createElementVNode("input", {
                type: "checkbox",
                "onUpdate:modelValue": _cache[6] || (_cache[6] = ($event: any) => ((isMaxStoneSize).value = $event)),
                class: "peer sr-only"
              }, null, 512), [
                [_vModelCheckbox, isMaxStoneSize.value]
              ]),
              _cache[10] || (_cache[10] = _createElementVNode("div", { class: "w-5 h-5 border-2 border-gray-200 rounded-md transition-all duration-200 peer-checked:border-brand-main peer-checked:bg-brand-main group-hover:border-gray-300" }, null, -1)),
              _cache[11] || (_cache[11] = _createElementVNode("svg", {
                class: "absolute w-3.5 h-3.5 text-white scale-0 transition-transform duration-200 peer-checked:scale-100",
                xmlns: "http://www.w3.org/2000/svg",
                viewBox: "0 0 24 24",
                fill: "none",
                stroke: "currentColor",
                "stroke-width": "4",
                "stroke-linecap": "round",
                "stroke-linejoin": "round"
              }, [
                _createElementVNode("polyline", { points: "20 6 9 17 4 12" })
              ], -1))
            ]),
            _createElementVNode("div", _hoisted_10, [
              _createElementVNode("span", _hoisted_11, _toDisplayString(_unref(t)('components.catalog.filter.price_sort.max-stone-size.label')), 1),
              _createElementVNode("span", _hoisted_12, _toDisplayString(_unref(t)('components.catalog.filter.price_sort.max-stone-size.description')), 1)
            ])
          ])
        ]))
      : _createCommentVNode("", true),
    (isMinPriceButtonAllowed.value)
      ? (_openBlock(), _createElementBlock("div", _hoisted_13, [
          _createElementVNode("label", _hoisted_14, [
            _createElementVNode("div", _hoisted_15, [
              _withDirectives(_createElementVNode("input", {
                type: "checkbox",
                "onUpdate:modelValue": _cache[7] || (_cache[7] = ($event: any) => ((isMinPrice).value = $event)),
                class: "peer sr-only"
              }, null, 512), [
                [_vModelCheckbox, isMinPrice.value]
              ]),
              _cache[12] || (_cache[12] = _createElementVNode("div", { class: "w-5 h-5 border-2 border-gray-200 rounded-md transition-all duration-200 peer-checked:border-brand-main peer-checked:bg-brand-main group-hover:border-gray-300" }, null, -1)),
              _cache[13] || (_cache[13] = _createElementVNode("svg", {
                class: "absolute w-3.5 h-3.5 text-white scale-0 transition-transform duration-200 peer-checked:scale-100",
                xmlns: "http://www.w3.org/2000/svg",
                viewBox: "0 0 24 24",
                fill: "none",
                stroke: "currentColor",
                "stroke-width": "4",
                "stroke-linecap": "round",
                "stroke-linejoin": "round"
              }, [
                _createElementVNode("polyline", { points: "20 6 9 17 4 12" })
              ], -1))
            ]),
            _createElementVNode("div", _hoisted_16, [
              _createElementVNode("span", _hoisted_17, _toDisplayString(_unref(t)('components.catalog.filter.price_sort.min-price.label')), 1),
              _createElementVNode("span", _hoisted_18, _toDisplayString(_unref(t)('components.catalog.filter.price_sort.min-price.description')), 1)
            ])
          ])
        ]))
      : _createCommentVNode("", true)
  ]))
}
}

})